import axios from 'axios';
import moment from 'moment';
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';

import {
  Alert,
  Box,
  Button,
  Card,
  Checkbox,
  DatePicker,
  Dialog,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  Paper,
  Radio,
  RadioGroup,
  Skeleton,
  TextField,
  Typography,
  ViewContainer,
} from 'components/AORedesign';
import {
  ADD_DOC_NEWS,
  ADD_TEXT_NEWS,
  ALL_STATES_FOR_NEWS,
  DELETE_ARTICLE,
  GET_NEWS_ARTICLES,
  UPDATE_DOC_ARTICLE,
  UPDATE_TEXT_ARTICLE,
} from '../../../../constants/api';
import featureFlags from '../../../../constants/featureFlags';
import { DocUploader } from '../../../AORedesign/DocUploader';
import { AOTransferList } from '../../../AOTransferList';
import { handleFromDateOnChange, handleOnChange } from '../../../CommonUtilityServices/EventHandlerService';
import { Feature } from '../../../Feature';
import SearchNewsArticlesTypeRenderCell from '../SearchNewsArticles/SearchNewsArticlesTypeRenderCell';

const initFormData = {
  title: '',
  startDate: moment().format('MM/DD/YYYY'),
  endDate: null,
  status: 'D',
  type: '',
};

const initDisplaySection = {
  0: false,
  1: false,
  2: false,
  3: false,
  4: false,
};

const initAlert = { severity: '', message: '' };

const NEWS_TYPE_TEXT = 'T';
const NEWS_TYPE_DOC = 'D';

const AddOrEditNewsArticle = ({ newsId, searchEdit, handleSetNewsArticleId }) => {
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState(initFormData);
  const [displaySection, setDisplaySection] = useState(initDisplaySection);
  const [availableStates, setAvailableStates] = useState([]);
  const [checkedStates, setCheckedStates] = useState([]);
  const [selectedStates, setSelectedStates] = useState([]);
  const [alert, setAlert] = useState(initAlert);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const initState = useRef({
    availableStates: [],
    selectedStates: [],
    formData: initFormData,
    displaySection: initDisplaySection,
  });

  useEffect(() => {
    (async () => {
      if (searchEdit && newsId) {
        const { data } = await axios.get(`${GET_NEWS_ARTICLES}/${newsId}`);
        const newsArticle = data;
        const formDataById = {
          seqId: newsArticle.seqId,
          title: newsArticle.title,
          startDate: newsArticle.startDate,
          endDate: newsArticle.endDate,
          status: newsArticle.status,
          extension: newsArticle.extension,
          type: newsArticle.type,
          text: newsArticle.text,
          sectionsString: newsArticle.sectionsString,
          selectedStatesString: newsArticle.selectedStatesString,
        };
        setFormData(formDataById);
        initState.current.formData = formDataById;

        const newDisplaySection = { ...initDisplaySection };
        for (const section of newsArticle.sections) {
          newDisplaySection[section] = true;
        }
        setDisplaySection(newDisplaySection);
        initState.current.displaySection = newDisplaySection;

        setAvailableStates(newsArticle.availableStates);
        initState.current.availableStates = newsArticle.availableStates;
        setSelectedStates(newsArticle.selectedStates);
        initState.current.selectedStates = newsArticle.selectedStates;
      } else {
        const getStatesForNews = await getStates();
        setAvailableStates(getStatesForNews);
        initState.current.availableStates = getStatesForNews;
      }
      setLoading(false);
    })();
  }, []);

  const handleOnChangeCheckbox = (e) => {
    const {
      target: { checked, name },
    } = e;
    const newDisplaySection = { ...displaySection };
    newDisplaySection[name] = checked;
    setDisplaySection(newDisplaySection);
  };

  /* Callback fired when the From Date value changes. */
  const handleOnChangeStartDate = (date) => {
    handleFromDateOnChange(date, formData, setFormData, 'startDate');
  };

  const resetForm = () => {
    setCheckedStates([]);
    setAvailableStates(initState.current.availableStates);
    setSelectedStates(initState.current.selectedStates);
    setFormData(initState.current.formData);
    setDisplaySection(initState.current.displaySection);
  };

  const handleClickSubmit = async () => {
    setAlert(initAlert);
    try {
      const keys = Object.keys(displaySection);
      const filteredSection = keys.filter((key) => {
        return displaySection[key];
      });

      const TEXT_DOCUMENT = formData.type === NEWS_TYPE_TEXT;

      const requestBody = {
        title: formData.title,
        type: formData.type,
        startDate: formData.startDate,
        endDate: formData.endDate ?? '',
        status: formData.status,
        sectionsString: filteredSection.join(','),
        selectedStatesString: selectedStates.join(','),
        text: formData.text,
        docContent: formData.docContent,
        extension: formData.extension,
      };

      // Update article
      if (newsId) {
        requestBody.seqId = formData.seqId;
        requestBody.selectedStates = selectedStates;
        requestBody.sections = filteredSection;

        const { status } = await await axios.put(TEXT_DOCUMENT ? UPDATE_TEXT_ARTICLE : UPDATE_DOC_ARTICLE, requestBody);

        if (status >= 200 && status < 300) {
          setAlert({ severity: 'success', message: 'News item successfully updated.' });
          initState.current = {
            formData: formData,
            availableStates: availableStates,
            selectedStates: selectedStates,
            displaySection: displaySection,
          };
        } else {
          setAlert({ severity: 'error', message: 'Failed to update news item. Please try again later.' });
        }
      }
      // Create new article
      else {
        const { status } = await await axios.post(TEXT_DOCUMENT ? ADD_TEXT_NEWS : ADD_DOC_NEWS, requestBody);

        if (status >= 200 && status < 300) {
          setAlert({ severity: 'success', message: 'News item successfully created.' });
          resetForm();
        } else {
          setAlert({ severity: 'error', message: 'Failed to create news item. Please try again later.' });
        }
      }
    } catch (error) {
      setAlert({ severity: 'error', message: error.message });
    }
  };

  /* set document extension and base64Data for document news article*/
  const setDocumentDetails = (documentDetails) => {
    setFormData((prevState) => ({
      ...prevState,
      docContent: documentDetails.docContent,
      extension: documentDetails.extension,
    }));
  };

  const handleDeleteOnClick = async () => {
    const { status } = await axios.delete(`${DELETE_ARTICLE}/${newsId}`);

    if (status >= 200 && status < 300) {
      handleSetNewsArticleId(null);
    }
  };

  const isSubmitDisabled = () =>
    !formData?.title?.length ||
    !formData?.startDate?.length ||
    !Object?.values(displaySection)?.includes(true) ||
    !selectedStates?.length ||
    !formData?.type?.length ||
    (formData?.type === NEWS_TYPE_TEXT && !formData?.text?.length) ||
    (!searchEdit && formData?.type === NEWS_TYPE_DOC && !formData?.docContent?.length && !formData?.extension?.length);

  const getStates = async () => {
    const { data } = await axios.get(ALL_STATES_FOR_NEWS);
    return data;
  };

  return (
    <Feature allowed={featureFlags.ACCESS_CONTENT_MGMT_ADMIN}>
      <ViewContainer title="News Category Agents" hideStartQuote hidePolicyViewChange>
        <Card>
          {loading ? (
            <Skeleton variant="rectangular" height={250} />
          ) : (
            <Box p={1}>
              <Box display="flex">
                <Box mr={3}>
                  <Box mb={4}>
                    <TextField
                      style={{ width: '700px' }}
                      id={'title'}
                      label={'Title'}
                      value={formData.title}
                      name={'title'}
                      required
                      onChange={(e) => handleOnChange(e, formData, setFormData)}
                    />
                  </Box>
                  <Box mb={4}>
                    <DatePicker
                      id={`start-datepicker`}
                      name={`startDate`}
                      label={`Display Start Date`}
                      value={formData.startDate}
                      onChange={(date) => handleOnChangeStartDate(date)}
                      required
                    />
                  </Box>
                </Box>
                <FormControl required component="fieldset">
                  <FormLabel component="legend">Display Section</FormLabel>
                  <FormGroup>
                    <FormControlLabel
                      control={<Checkbox checked={displaySection[0]} onChange={(e) => handleOnChangeCheckbox(e)} name="0" />}
                      label="Main Street America"
                    />
                    <FormControlLabel
                      control={<Checkbox checked={displaySection[4]} onChange={(e) => handleOnChangeCheckbox(e)} name="4" />}
                      label="Commercial"
                    />
                    <FormControlLabel
                      control={<Checkbox checked={displaySection[3]} onChange={(e) => handleOnChangeCheckbox(e)} name="3" />}
                      label="Personal"
                    />
                    <FormControlLabel
                      control={<Checkbox checked={displaySection[1]} onChange={(e) => handleOnChangeCheckbox(e)} name="1" />}
                      label="Dashboard and bell menu alert"
                    />
                    <FormControlLabel
                      control={<Checkbox checked={displaySection[2]} onChange={(e) => handleOnChangeCheckbox(e)} name="2" />}
                      label="Bell menu alert only"
                    />
                  </FormGroup>
                </FormControl>
              </Box>

              <Box mb={8}>
                <AOTransferList
                  required
                  checked={checkedStates}
                  left={availableStates}
                  leftLabel={'Available States'}
                  right={selectedStates}
                  rightLabel={'* Selected States'}
                  setLeft={(e) => setAvailableStates(e)}
                  setRight={(e) => setSelectedStates(e)}
                  setChecked={setCheckedStates}
                />
                {selectedStates.length === 0 && (
                  <Typography component="div" variant="body1">
                    <Box color="text.secondary" fontSize={'0.75rem'} mt={4}>
                      You must select a State
                    </Box>
                  </Typography>
                )}
              </Box>

              <Box ml={2} mb={6}>
                <FormControl component="fieldset" required>
                  <FormLabel component="legend">Display Status</FormLabel>
                  <RadioGroup aria-label="displayStatus" name="status" value={formData.status} onChange={(e) => handleOnChange(e, formData, setFormData)}>
                    <FormControlLabel value="D" control={<Radio />} label="Display" />
                    <FormControlLabel value="H" control={<Radio />} label="Hide Alert" />
                  </RadioGroup>
                </FormControl>
              </Box>

              {!searchEdit && (
                <Box ml={2} mb={2}>
                  <FormControl component="fieldset" required>
                    <RadioGroup aria-label="displayStatus" name="type" value={formData.type} onChange={(e) => handleOnChange(e, formData, setFormData)}>
                      <FormControlLabel value={NEWS_TYPE_DOC} control={<Radio />} label="Link to Announcement Document" />
                      <FormControlLabel value={NEWS_TYPE_TEXT} control={<Radio />} label="Alert Text" />
                    </RadioGroup>
                  </FormControl>
                </Box>
              )}

              <Box mb={2}>
                {formData.type === NEWS_TYPE_DOC && !searchEdit && (
                  <DocUploader
                    label={searchEdit ? 'Upload Doc.?' : 'Document *'}
                    acceptableFileTypes=".doc, .pdf, .docx"
                    invalidFileTypeMsg="Please select an Adobe Acrobat (*.pdf) or Microsoft Word (*.doc or *.docx) file to upload."
                    setDocumentDetails={setDocumentDetails}
                  />
                )}
                {formData.type === NEWS_TYPE_DOC && searchEdit && (
                  <>
                    <Typography>Download document</Typography>
                    <SearchNewsArticlesTypeRenderCell
                      type={initState.current.formData.type}
                      extension={initState.current.formData.extension}
                      seqId={initState.current.formData.seqId}
                      handleOpenForNewsReview={() => {}}
                    />
                  </>
                )}
                {formData.type === NEWS_TYPE_TEXT && (
                  <TextField
                    disabled={searchEdit}
                    fullWidth
                    multiline
                    name="text"
                    value={formData.text}
                    onChange={(e) => handleOnChange(e, formData, setFormData)}
                    label="Article Body"
                    minRows={4}
                  />
                )}
              </Box>

              {alert.message.length > 0 && (
                <Box mb={2}>
                  <Alert severity={alert.severity} message={alert.message} />
                </Box>
              )}

              <Box ml={2}>
                <Box display="flex" justifyContent="flex-end">
                  <Box>
                    <Box display="flex">
                      <Box>
                        <Button color="primary" disabled={isSubmitDisabled()} onClick={handleClickSubmit}>
                          Submit
                        </Button>
                      </Box>
                      <Box mx={2}>
                        <Button type="reset" color={'default'} onClick={resetForm}>
                          Reset
                        </Button>
                      </Box>
                      {searchEdit && newsId && (
                        <Box mr={2}>
                          <Button color="primary" variant="outlined" onClick={() => setShowDeleteModal(true)}>
                            Delete
                          </Button>
                        </Box>
                      )}
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Box>
          )}
        </Card>
      </ViewContainer>

      <Dialog
        open={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        aria-labelledby="search-edit-news-article-delete"
        aria-describedby="search-edit-news-article-delete-modal"
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Paper style={{ padding: `1rem` }}>
          <Box p={3} mb={4}>
            You are about to delete this news item. Continue?
          </Box>
          <Box mt={2} display="flex" justifyContent={'flex-end'}>
            <Button id="search-edit-news-article-delete-modal-no-button" onClick={() => setShowDeleteModal(false)} variant="outlined">
              No
            </Button>
            <Box mr={1} />
            <Button color="primary" id="search-edit-news-article-delete-modal-yes-button" onClick={handleDeleteOnClick} variant="contained">
              Yes
            </Button>
          </Box>
        </Paper>
      </Dialog>
    </Feature>
  );
};

AddOrEditNewsArticle.defaultProps = {
  searchEdit: false,
};

AddOrEditNewsArticle.propTypes = {
  newsId: PropTypes.number,
  searchEdit: PropTypes.bool,
  handleSetNewsArticleId: PropTypes.func,
};

export default AddOrEditNewsArticle;
